import type { ModelMark } from "@/components/dashboard/model-logos";
import type { TimeseriesResponse } from "@/lib/api/types";
import { formatPkrPrice, PRICE_NOT_SET } from "@/lib/api/format";

/**
 * Routes that are no longer offered, and must not be named anywhere in the UI.
 *
 * Only `p9-reason`, the renamed alias verified routable in the 2026-08-10 audit.
 * It is dead: measured 2026-08-20 against the live analytics API, its last 24
 * hours were 1 request, **0 tokens**, 7ms average latency, which is the
 * signature of a call that failed before it reached a model. Removing it from
 * copy is not enough, because it keeps appearing in analytics for the traffic
 * it served, so anything that lists model identities has to drop it too or the
 * dashboard offers a route that no longer exists. Aggregates deliberately keep
 * it (see `withoutRetiredRoutes`): retiring a route does not un-send its
 * requests.
 *
 * **`gpt-oss-120b(high)` was briefly listed here and must not be re-added
 * without evidence.** It was assumed dead when the catalogue moved to
 * `...(Global)`; the same measurement showed it serving 19 requests, 1,484
 * tokens and ~4s latency in 24 hours. The two ids are both live, they are not a
 * rename, and the catalogue offers `(Global)` because that is the one P/9 chose
 * to sell, not because the other stopped working. Retiring a live route would
 * have hidden real traffic from the panel whose whole job is to show it.
 *
 * The test for this set is a measurement, not a name: requests with zero tokens
 * and near-zero latency are a dead alias, anything producing tokens is alive.
 *
 * Entries must be lowercase: the comparison lowercases the incoming group, so a
 * mixed-case entry here would silently never match.
 *
 * Compared case-insensitively against the exact identifier, not by prefix. A
 * prefix match would also swallow any future `p9-reason-v2`, which is a
 * different decision than the one being made here.
 */
const RETIRED_ROUTES = new Set(["p9-reason"]);

export function isRetiredRoute(group: string): boolean {
  return RETIRED_ROUTES.has(group.trim().toLowerCase());
}

/**
 * Drop retired routes from a grouped timeseries response.
 *
 * Only for panels that **name** models: per-model chart series, the observed
 * traffic list, attribution breakdowns, the key-scope chips. Aggregate panels
 * (the request heatmap, the summary tiles) deliberately do not use this, so
 * total requests and tokens still describe the traffic that really happened.
 * Retiring a route does not un-send the requests it served.
 *
 * Returns a new object, so callers must invoke it inside the `useMemo` that
 * already depends on `data`, never in a render body feeding an effect
 * dependency. See `kb/design.md` on `StatTile`/`CountUp` for what that costs.
 */
export function withoutRetiredRoutes(res: TimeseriesResponse): TimeseriesResponse {
  const data = res.data.filter((p) => !isRetiredRoute(p.group));
  return data.length === res.data.length ? res : { ...res, data };
}

/* ------------------------------------------------------------- catalogue */

/**
 * Where a route's inference actually runs, stated by the team on 2026-08-24.
 *
 * Two pools. `pk-npu` is P/9's own Ascend 910B NPU capacity in Pakistan, the
 * hardware the platform is built around. `global` is pass-through: the gateway
 * relays the request to partner infrastructure outside that pool (Baseten
 * today, per the backend team) and bills it in rupees like everything else.
 * The distinction is real and operational, not marketing: the two pools fail
 * independently (the PK pool's 2026-08-21 outage took `qwen` routes and
 * `gpt-oss-120b(high)` down while both `(Global)` routes kept serving), so a
 * caller choosing a route is also choosing whose outage they inherit.
 */
export type Deployment = "pk-npu" | "global";

/** Short per-row tag for pickers and identity rows; the section title is long. */
export const DEPLOYMENT_LABEL: Record<Deployment, string> = {
  "pk-npu": "PK NPU",
  global: "Global",
};

/**
 * Whether a route is open to callers today.
 *
 * `coming-soon` rows stay in the catalogue (the team wants them visible) but
 * wear a tag, carry a notice in their drawer, and are kept out of the
 * playground picker: a picker that offers a route which answers 404 or times
 * out is worse than one that does not list it. Stated per row rather than per
 * pool, so the first PK route to open flips one field and nothing else.
 */
export type RouteStatus = "live" | "coming-soon";

/**
 * The Model APIs page's two catalogue sections, in display order. Global
 * pass-through first since 2026-08-25, on instruction. (The order was
 * PK-first for one day, 2026-08-24, on the earlier instruction; both are
 * recorded in `kb/decisions.md`.) Both pools answer: the PK NPU-native routes
 * opened on `akbl` 2026-08-27 and on `main` 2026-10-21, on instruction. Copy
 * lives here beside the `deployment` values it describes so the two cannot
 * drift.
 */
export const DEPLOYMENT_SECTIONS: Array<{
  deployment: Deployment;
  title: string;
  description: string;
}> = [
  {
    deployment: "global",
    title: "Global pass-through",
    description:
      "Relayed by the gateway to partner capacity outside the PK NPU pool. Same endpoint, same keys, billed in rupees like every other route.",
  },
  {
    deployment: "pk-npu",
    // "PK Endpoints" since 2026-09-22, on instruction, matching the PK Endpoint
    // page. The hardware is named in the description below instead.
    title: "PK Endpoints",
    description:
      "Served from P/9's own Ascend 910B NPU capacity in Pakistan, the platform's native hardware. Open with the same keys and the same rupee billing as every other route.",
  },
];

export interface ModelBenchmark {
  /** Short axis label for the radar plot. */
  key: string;
  /** Full suite name for the labelled bar. */
  label: string;
  /** Published score on a 0-100 scale, exactly as the lab printed it. */
  score: number;
}

/**
 * Published facts about the open weights behind a route, for the model card
 * drawer. Everything here is a claim about the model as its lab released it,
 * sourced from the lab's own model card or paper, never a measurement from the
 * P/9 gateway; the drawer says so on the panel. That framing is what makes this
 * data admissible under the no-placeholder rule when the invented six-model
 * pedigree was not: these numbers have an owner and a citation.
 */
export interface ModelPedigree {
  lab: string;
  parameters: string;
  license: string;
  /** Month-and-year string, printed verbatim; never parsed. */
  released: string;
  /** The published native context of the weights, not the gateway's limit. */
  contextWindow: string;
  /** One paragraph of what the model is, carrying no numbers. */
  summary: string;
  /**
   * "What it's good for": advisory bullets, shown first in the drawer.
   * Grounded in the lab's published capabilities and in properties of the P/9
   * route (its pinned effort, its meter), never in measurements we do not
   * have. A use the platform cannot deliver (fine-tuning, a `dimensions`
   * parameter) must not appear here even if the weights support it.
   */
  goodFor: string[];
  /** Model-specific facts that do not fit the fixed grid. */
  details: Array<{ label: string; value: string }>;
  benchmarks: ModelBenchmark[];
  /** Where the scores come from; printed under the charts. */
  benchmarkNote: string;
  paperUrl: string;
  paperLabel: string;
}

export type ModelKind = "chat" | "embedding" | "decision";

/** How each kind is named: `short` for badges, `long` for card lines. */
export const KIND_LABEL: Record<ModelKind, { short: string; long: string; route: string }> = {
  chat: { short: "Chat", long: "Chat completions", route: "Chat completion route" },
  embedding: { short: "Embedding", long: "Embeddings", route: "Embedding route" },
  decision: { short: "Decision", long: "Decisions", route: "Decision route" },
};

export interface CatalogModel {
  /** Exactly what goes in a request's `model` field, punctuation included. */
  id: string;
  /**
   * True when `id` is a string P/9 has **proposed** rather than one the gateway
   * answers to today.
   *
   * Announced-ahead-of-opening routes need an id for the same reasons a live
   * one does (React keys, the slot lookup on the landing cards, the code
   * snippets), but printing a proposed id with no qualifier hands a caller a
   * string that returns `400 unknown model alias`. So every surface that
   * prints an id also prints this. Absent means the string is confirmed;
   * clear the flag per row as the backend registers each one.
   *
   * Orthogonal to `status`. A route can be coming soon with a confirmed id
   * (`gpt-oss-120b(high)` answers today, the pool is simply not open), and in
   * principle an id could be renamed under a live route. Do not collapse them.
   */
  idProvisional?: boolean;
  /** Display name. Never used as identity. */
  name: string;
  /**
   * What the route answers. `decision` (2026-10-13) is neither chat nor
   * embedding: text plus typed questions in, a probability per option out, on
   * its own endpoint (`POST /v1/systemone`). Labels come from `KIND_LABEL`, so
   * a fourth kind is one entry there rather than a ternary per page.
   */
  kind: ModelKind;
  /**
   * `"image"` for a chat route whose request must carry a picture (document
   * OCR), so a text-only prompt is the wrong example. Its snippets send an
   * image instead of `PROMPT`, and it is kept out of the playground, which
   * only sends text. Absent means an ordinary text chat route.
   */
  input?: "image";
  /**
   * Which pool serves this route; see `Deployment`. Stated per row rather than
   * sniffed out of the id: "(Global)" in an id does mark the pass-through rows
   * today, but that is a naming habit, not a wire contract, and a substring
   * match would silently misfile the first route named differently.
   */
  deployment: Deployment;
  /** Open to callers today, or listed ahead of opening; see `RouteStatus`. */
  status: RouteStatus;
  /**
   * Which upstream lab's mark to show, from `MODEL_MARKS` in
   * `components/dashboard/model-logos.tsx`. Stated here rather than sniffed out
   * of `id`: the ids are ours, so a substring match on "qwen" would break on
   * the first rename, and break invisibly.
   */
  logo: ModelMark | null;
  /** Weights on Hugging Face, where we have a confirmed link. */
  weightsUrl: string | null;
  /**
   * The lab that published the weights, for rows carrying no `pedigree`.
   *
   * Redundant with `pedigree.lab` where a pedigree exists, and deliberately
   * so: `pedigree` is an all-or-nothing block sourced from a lab's own model
   * card, and a row whose card nobody has read still has a knowable owner.
   * The card's second line falls back to this before it falls back to the
   * generic "Chat completion route".
   */
  lab?: string;
  /**
   * One line of what the model is, for rows carrying no `pedigree`.
   *
   * Written here in P/9's voice from facts the upstream listing publishes
   * (parameter counts, context, modality, what it is tuned for), never copied
   * from a vendor's page and never carrying a number nobody has sourced. Rows
   * whose upstream listing says nothing simply omit it: the card falls back to
   * saying there is no published model card yet, which is true.
   */
  blurb?: string;
  /**
   * PKR per million input tokens, or null when P/9 has not confirmed a price
   * for this route yet. Null prints `PRICE_NOT_SET`; it never falls back to a
   * neighbouring row's figure, because a wrong price on a billing page is
   * worse than an absent one.
   */
  inputPkrPerMillion: number | null;
  /**
   * PKR per million **cached** prompt tokens: the part of a prompt the model
   * provider served from its cache, billed at this rate instead of the input
   * rate (Ticket 064, billed since 2026-10-26). Null means the model has no
   * cached price, so its cached tokens cost the input price: it prints "Same
   * as input", never "Not set", because the price is known.
   */
  cachedInputPkrPerMillion: number | null;
  /**
   * PKR per million output tokens.
   *
   * Three states, and the difference matters to a reader deciding what a call
   * will cost: a number is the price, `"none"` means the model emits no output
   * tokens at all (embeddings return vectors), and null means the price is
   * simply not confirmed yet. Collapsing the last two into one null, which is
   * what this field used to be, would have shown a chat model as billing
   * nothing for output.
   */
  outputPkrPerMillion: number | "none" | null;
  /**
   * Published provenance and benchmarks for the drawer, or null for a route
   * whose upstream weights have no citable card. Null degrades the drawer to
   * its Code tab rather than inviting anyone to fill this in from memory.
   */
  pedigree: ModelPedigree | null;
}

/**
 * The routes P/9 serves.
 *
 * This is a **frontend-curated catalogue**, which the pre-launch mask removed
 * once already, and it comes back here deliberately and narrowly. The reasons
 * it is defensible: every price is a confirmed one rather than an invented
 * one, and a route whose price nobody has confirmed prints `PRICE_NOT_SET`
 * instead of borrowing a plausible number from the row above it. No row is in
 * that state as of 2026-08-30: the PK-pool gpt-oss route, unpriced since it
 * was added, took the rate below once the team confirmed their gpt-oss quote
 * covers both pools.
 *
 * Until 2026-08-30 the list said every id was one the backend actually serves.
 * It no longer is. The eleven partner routes announced that day are real
 * models with real published rates, but their ids are ours to propose until
 * the gateway registers them, so they carry `idProvisional` and every surface
 * that prints an id says so out loud. That is the narrow version of the same
 * rule: it is fine to list a route ahead of opening, it is not fine to let a
 * reader believe a string will answer when it will not. Ten still carry it:
 * GLM-5.2 was registered and opened on 2026-09-07 and dropped both flags.
 * What has not changed is the risk. There is still no
 * `GET /api/models` (404, re-verified 2026-08-20 against the public edge), so
 * nothing reconciles these numbers against the gateway, and a price changed
 * upstream makes this table quietly wrong. Treat it as copy that expires, and
 * replace it with the endpoint the moment one exists. Recorded in
 * `kb/decisions.md`.
 *
 * Chat routes first, embeddings last: the playground defaults to
 * `CHAT_MODELS[0]`, so the order here decides what a first-time visitor sends.
 *
 * Every id carries a `/`. That used to be the tell for "resolved upstream id,
 * not something you can send"; it is not any more, and `useObservedModels` was
 * corrected to match. Ids are copied **verbatim** from what the backend team
 * deployed, punctuation and capitalisation included: `Kimi-K3(Global)` is
 * mixed-case and parenthesised on purpose, and normalising it would break every
 * snippet on the page.
 */
/**
 * The gpt-oss 120B weights, cited once and shared by both routes that serve
 * them. Two rows carry this object because P/9 serves the same weights from
 * both pools; duplicating the block would invite the copies drifting apart.
 *
 * Sources, both read 2026-08-15: the gpt-oss model card paper
 * (arxiv.org/abs/2508.10925) and the Hugging Face model card. Benchmark rows
 * are the paper's no-tools numbers at high reasoning effort.
 *
 * Every "this route pins high effort" claim was removed 2026-08-20. The only
 * evidence for it was that the id we shipped ended in `(high)`; both ids are
 * live and serving (measured the same day), so `(high)` was never a rename we
 * could read an effort out of, and `(Global)` says nothing about effort at
 * all. No endpoint reports what the gateway sends. Restore the claim only when
 * someone confirms the effort, not because the benchmark rows still happen to
 * be high-effort numbers.
 */
const GPT_OSS_PEDIGREE: ModelPedigree = {
  lab: "OpenAI",
  parameters: "117B total, 5.1B active",
  license: "Apache 2.0",
  released: "August 2025",
  contextWindow: "131,072 tokens",
  summary:
    "OpenAI's open-weights reasoning model: a mixture-of-experts transformer released with full chain-of-thought access and three switchable reasoning efforts, low, medium and high.",
  goodFor: [
    "Agentic workflows: the model card lists native function calling, tool use and structured outputs among its design goals",
    "Reasoning-heavy work in maths, science and code, where its published scores concentrate",
    "Deliberate answers over fast ones: these weights think before they answer, and the completion budget pays for that thinking as well as the visible reply",
  ],
  details: [
    { label: "Architecture", value: "Mixture of experts, MXFP4-quantised expert weights" },
    { label: "Reasoning efforts", value: "Low, medium, high (which this route sends is not reported)" },
  ],
  benchmarks: [
    { key: "MMLU", label: "MMLU, general knowledge", score: 90.0 },
    { key: "AIME '25", label: "AIME 2025, competition maths", score: 92.5 },
    { key: "GPQA", label: "GPQA Diamond, graduate science", score: 80.1 },
    { key: "SWE-bench", label: "SWE-bench Verified, software engineering", score: 62.4 },
    { key: "HLE", label: "Humanity's Last Exam, frontier knowledge", score: 14.9 },
  ],
  benchmarkNote:
    "As published in OpenAI's gpt-oss model card, at high reasoning effort without tools. Which effort this route sends is not reported by any endpoint, so these are the weights' published ceiling rather than a figure for this route. Not re-measured on P/9 hardware.",
  paperUrl: "https://arxiv.org/abs/2508.10925",
  paperLabel: "gpt-oss model card",
};

export const CATALOG_MODELS: CatalogModel[] = [
  {
    id: "paranine/gpt-oss-120b(Global)",
    name: "gpt-oss 120B",
    kind: "chat",
    // Stated by the team 2026-08-24: the (Global) routes are the pass-through
    // pool (Baseten). Consistent with the 2026-08-21 outage, which this route
    // rode out while the PK pool was down.
    deployment: "global",
    status: "live",
    // gpt-oss is OpenAI's open-weights release, so the mark is theirs.
    logo: "openai",
    // Repo confirmed to exist 2026-08-15 (its model card was fetched from it).
    weightsUrl: "https://huggingface.co/openai/gpt-oss-120b",
    // Repriced 2026-08-30 on instruction, down from Rs 50 / Rs 150. The rate
    // was quoted for "gpt-oss" with no pool named, and confirmed as covering
    // both gpt-oss rows, so the two carry identical figures on purpose.
    // Ticket 064 (2026-10-26): cached input $0.10 and output $0.50, the
    // provider's list price now that cached tokens are priced on their own.
    inputPkrPerMillion: 30.08,
    cachedInputPkrPerMillion: 30.078,
    outputPkrPerMillion: 150.39,
    pedigree: GPT_OSS_PEDIGREE,
  },
  {
    // Verbatim from the request body the backend team shipped on 2026-08-20,
    // "(Global)" and the capital K included. Do not tidy the casing.
    id: "paranine/Kimi-K3(Global)",
    name: "Kimi K3",
    kind: "chat",
    // Stated by the team 2026-08-24: pass-through (Baseten).
    deployment: "global",
    status: "live",
    // Moonshot AI ships the weights; Kimi is the brand on the mark.
    logo: "kimi",
    // Repo confirmed to exist 2026-08-20 (its model card was fetched from it).
    weightsUrl: "https://huggingface.co/moonshotai/Kimi-K3",
    // Confirmed 2026-08-20, hours after the row shipped unpriced. Output
    // repriced 2026-08-30 on instruction, up from Rs 4,511.70. Input unchanged.
    // Ticket 064 (2026-10-26): cached input $0.30 and output $15.00, the
    // provider's list price now that cached tokens are priced on their own.
    inputPkrPerMillion: 902.34,
    cachedInputPkrPerMillion: 90.234,
    outputPkrPerMillion: 4511.7,
    // Sources, both read 2026-08-20: the Kimi K3 technical report
    // (arxiv.org/abs/2607.24653, "Kimi K3: Open Frontier Intelligence") and the
    // Hugging Face model card. Benchmark rows are the card's headline figures,
    // one per family it reports (reasoning, terminal agents, software
    // engineering, web research, computer use), skipping every row the card
    // prints as a pair of numbers, because which half is which is not stated
    // on the page and guessing would put an unsourced number on the chart.
    pedigree: {
      lab: "Moonshot AI",
      parameters: "2.8T total, 104B active",
      license: "Kimi K3 License",
      released: "July 2026",
      contextWindow: "1,048,576 tokens",
      summary:
        "Moonshot AI's open-weights frontier model: a sparse mixture of experts built on Kimi Delta Attention and attention residuals, natively multimodal over text, images and video, and released with thinking always on. It is the largest set of weights behind any route here.",
      goodFor: [
        "Long-horizon agentic work: its published scores concentrate in the browsing, tool-use and terminal-agent suites",
        "Hard reasoning in science, maths and code, where it leads the open-weight field on the suites its card reports",
        "Long inputs: the published window is the largest of any route here, though the gateway's own enforced limit is not reported by any endpoint",
      ],
      details: [
        { label: "Architecture", value: "Mixture of experts, 896 experts, 16 per token" },
        { label: "Attention", value: "93 layers: 69 Kimi Delta Attention, 24 gated MLA" },
        { label: "Published modalities", value: "Text, image and video in; text out" },
      ],
      benchmarks: [
        { key: "GPQA", label: "GPQA Diamond, graduate science", score: 93.5 },
        { key: "T-Bench", label: "Terminal-Bench 2.1, terminal agents", score: 88.3 },
        { key: "DeepSWE", label: "DeepSWE, software engineering", score: 67.5 },
        { key: "Browse", label: "BrowseComp, web research", score: 91.2 },
        { key: "OSWorld", label: "OSWorld-Verified, computer use", score: 84.8 },
      ],
      benchmarkNote:
        "As published on Moonshot AI's Kimi K3 model card, read 2026-08-20. Not re-measured on P/9 hardware.",
      paperUrl: "https://arxiv.org/abs/2607.24653",
      paperLabel: "Kimi K3 technical report",
    },
  },
  // ADDED 2026-08-30, on instruction: eleven more routes from the partner
  // catalogue the (Global) pool passes through to, announced ahead of opening.
  // Everything about them is deliberately narrower than the two rows above.
  //
  //   * `idProvisional: true`. The ids follow the convention the two live
  //     (Global) rows set, but nobody has registered them, so they are ours to
  //     propose and not the gateway's to answer. Every surface that prints an
  //     id prints that qualifier with it. Clear the flag per row as each
  //     string is confirmed, never in bulk. Cleared for GLM-5.2 on
  //     2026-09-07 (since removed), and for GLM-5.3 and GLM-5.3 Fast on
  //     2026-10-16, each in the same edit that opened it.
  //   * `pedigree: null`. A model card here is sourced from the lab's own
  //     card, and none of these have been read. The drawer degrades to its
  //     Code tab rather than inviting anyone to fill the block in from memory,
  //     which is the rule that removed the invented six-model pedigree. `lab`
  //     and `blurb` carry the little that is knowable without it.
  //   * `weightsUrl: null` for the same reason: an unverified Hugging Face URL
  //     is a 404 with our name on it.
  //
  // Prices are the partner's published USD list rates, read 2026-08-30,
  // converted at the constant the backend prices with (its System
  // configuration, as given in the 2026-10-16 GLM-5.3 fix note): Rs 278.50 to
  // the dollar times P/9's 8% markup, so **Rs = USD x 300.78**, rounded to the
  // paisa. This comment used to derive it as Rs 285.10 with a 5.5% markup
  // (300.7805), nearly the same product but not how the backend prices, and
  // 1 paisa off on GLM-5.3 Fast's output. It reproduces
  // Rs 30.08 and Rs 902.34 for the two live input rates exactly, and
  // Rs 4,511.70 for the Kimi K3 output rate the team quoted before repricing
  // it, which is the figure recorded in that row's comment. Reprice these
  // together if the rate moves: one row converted at a different rate is how a
  // price table starts lying.
  //
  // Two of the partner's list rates sit below what P/9 already charges for the
  // same weights (gpt-oss output and Kimi K3 output are listed there at $0.50
  // and $15.00, against the $0.60 and $15.30 the live rows imply). Those two
  // rows were priced by the team directly on 2026-08-30 and are left alone;
  // the gap is theirs to reconcile, not ours to average away.
  {
    // Opened 2026-10-16 on instruction, with GLM-5.3 Fast below, and the id
    // flag cleared with it: the team named both strings. Not verifiable on
    // the GCP gateway that day (api.paranine.com answered 503 "no healthy
    // upstream" to every request); the sovereign gateway answered 404 for
    // both.
    id: "paranine/GLM-5.3(Global)",
    name: "GLM-5.3",
    kind: "chat",
    deployment: "global",
    status: "live",
    logo: "zai",
    weightsUrl: null,
    lab: "Z.ai",
    blurb:
      "Z.ai's newest model for agentic engineering: coding, tool use and long-horizon agent work.",
    // Repriced 2026-10-16 from the backend's Baseten confirmation (input $1.40).
    // Since 2026-10-26 (Ticket 064) cached prompt tokens have their own price
    // ($0.14), so output is the provider's list price again ($4.40): until then
    // the backend folded the cache price into output. At USD x 300.78
    // (278.50 x 1.08).
    inputPkrPerMillion: 421.09,
    cachedInputPkrPerMillion: 42.1092,
    outputPkrPerMillion: 1323.43,
    pedigree: null,
  },
  {
    // Was `paranine/GLM-5.3 Fast(Global)` ("natively multimodal", $0.15 /
    // $0.50) until 2026-10-16. The backend confirmed against Baseten that the
    // route is `zai-org/GLM-5.3-Fast`: the speed tier of GLM-5.3, not a
    // smaller model. A preview at Baseten, limited to 15 requests and 500K
    // tokens a minute for all of Paranine together, which the blurb says.
    id: "paranine/GLM-5.3-Fast(Global)",
    name: "GLM-5.3 Fast",
    kind: "chat",
    deployment: "global",
    status: "live",
    logo: "zai",
    weightsUrl: null,
    lab: "Z.ai",
    blurb:
      "GLM-5.3 tuned for speed: the same model on a faster serving tier. A preview at the provider, rate-limited across all of Paranine.",
    // Input $2.10; cached input $0.21 and output $6.60, each on its own since
    // 2026-10-26 (Ticket 064; the cache price was folded into output until
    // then). At USD x 300.78 (278.50 x 1.08), matching the backend to the paisa.
    inputPkrPerMillion: 631.64,
    cachedInputPkrPerMillion: 63.1638,
    outputPkrPerMillion: 1985.15,
    pedigree: null,
  },
  // `paranine/GLM-5.2` (live since 2026-09-07) was removed from the catalogue
  // on 2026-10-16, on instruction, in favour of GLM-5.3 and GLM-5.3 Fast
  // above. Its past traffic still shows in analytics under its id; it is not
  // in `RETIRED_ROUTES`, which is for routes measured dead.
  // `paranine/GLM-5.2-Fast(Global)` (coming soon since 2026-08-30) was removed
  // on 2026-10-16 with the rest of GLM-5.2: nothing customer-facing names
  // GLM-5.2 any more (Asad, via the backend's GLM-5.3 fix note).
  {
    id: "paranine/Kimi-K2.7-Code(Global)",
    idProvisional: true,
    name: "Kimi K2.7 Code",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    logo: "kimi",
    weightsUrl: null,
    lab: "Moonshot AI",
    // No blurb: the partner's listing publishes none for this route, and the
    // name is not a licence to describe weights nobody here has read.
    // $0.95 / $4.00
    inputPkrPerMillion: 285.74,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 1203.12,
    pedigree: null,
  },
  {
    // Added live on 2026-09-28: the backend registered the route and the team
    // quoted both rates in rupees the same day, so this is the gateway's own
    // id rather than one proposed here. That is why there is no
    // `idProvisional` on it while its three siblings below still carry one.
    //
    // It leads the DeepSeek block rather than sitting in date order among
    // them, so the lab's first row is the route a reader can actually call.
    id: "paranine/DeepSeek-V4.1-Flash(Global)",
    name: "DeepSeek V4.1 Flash",
    kind: "chat",
    deployment: "global",
    status: "live",
    logo: "deepseek",
    weightsUrl: null,
    lab: "DeepSeek AI",
    // No blurb: nothing describing this route has been read at its source, and
    // a sentence invented for a model card is the kind of detail that gets
    // quoted back at us.
    // Ticket 064 (2026-10-26): cached input $0.007 and output $1.20, the
    // provider's list price now that cached tokens are priced on their own.
    inputPkrPerMillion: 90.23,
    cachedInputPkrPerMillion: 2.10546,
    outputPkrPerMillion: 360.94,
    pedigree: null,
  },
  {
    id: "paranine/DeepSeek-V4-Flash-0731(Global)",
    idProvisional: true,
    name: "DeepSeek-V4-Flash-0731",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    logo: "deepseek",
    weightsUrl: null,
    lab: "DeepSeek AI",
    blurb:
      "An open-weight mixture of experts, 284B total and 13B active, with a one-million-token context and selectable reasoning effort.",
    // $0.13 / $0.26
    inputPkrPerMillion: 39.1,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 78.2,
    pedigree: null,
  },
  {
    id: "paranine/DeepSeek-V4-Pro-0813(Global)",
    idProvisional: true,
    name: "DeepSeek V4 Pro 0813",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    logo: "deepseek",
    weightsUrl: null,
    lab: "DeepSeek AI",
    blurb: "The 0813 release of DeepSeek's 1.6T-parameter open frontier model.",
    // $1.32 / $3.96
    inputPkrPerMillion: 397.03,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 1191.09,
    pedigree: null,
  },
  {
    id: "paranine/DeepSeek-V4-Pro(Global)",
    idProvisional: true,
    name: "DeepSeek V4 Pro",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    logo: "deepseek",
    weightsUrl: null,
    lab: "DeepSeek AI",
    // No blurb: the partner's listing publishes none for this route.
    // $1.74 / $3.48
    inputPkrPerMillion: 523.36,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 1046.72,
    pedigree: null,
  },
  {
    id: "paranine/Nemotron-3-Ultra(Global)",
    idProvisional: true,
    name: "Nvidia Nemotron 3 Ultra",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    logo: "nvidia",
    weightsUrl: null,
    lab: "Nvidia",
    // No blurb: the partner's listing publishes none for this route.
    // $0.60 / $2.40
    inputPkrPerMillion: 180.47,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 721.87,
    pedigree: null,
  },
  {
    id: "paranine/Inkling(Global)",
    idProvisional: true,
    name: "Inkling",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    // Thinking Machines Lab ships a wordmark, not a glyph, and no icon set
    // carries it. Setting their name in our own type would be inventing a
    // trademark, so the row draws no mark. See `model-logos.tsx`.
    logo: null,
    weightsUrl: null,
    lab: "Thinking Machines Lab",
    blurb:
      "A multimodal mixture of experts, 975B total and 41B active, reasoning over text, images and audio at a 256k context.",
    // $1.00 / $4.05
    inputPkrPerMillion: 300.78,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 1218.16,
    pedigree: null,
  },
  {
    id: "paranine/Inkling-Small(Global)",
    idProvisional: true,
    name: "Inkling-Small",
    kind: "chat",
    deployment: "global",
    status: "coming-soon",
    logo: null,
    weightsUrl: null,
    lab: "Thinking Machines Lab",
    blurb:
      "The smaller, open-weight Inkling: faster and cheaper to run, with the same native text, image and audio input.",
    // $0.50 / $1.20
    inputPkrPerMillion: 150.39,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 360.94,
    pedigree: null,
  },
  // ADDED 2026-08-24: the team's deployment list names gpt-oss-120 in **both**
  // pools ("PK NPU: Qwen 3.8, Qwen embedding, GPT-oss-120" and "Global
  // pass-through: Kimi K3 baseten, gpt-oss-120 baseten"), so the catalogue
  // carries two rows for the same weights. `(high)` is the PK-pool alias by
  // elimination, not by declaration: it is the only other live gpt-oss alias
  // (measured serving 19 requests and 1,484 tokens on 2026-08-20), and it went
  // unreachable in the 2026-08-21 PK outage alongside `qwen3-embedding` while
  // both `(Global)` routes kept answering, which is the failure signature of
  // the PK pool. If the backend team names a different alias for the NPU
  // deployment, swap the id here; nothing else keys off it.
  {
    id: "paranine/gpt-oss-120b(high)",
    name: "gpt-oss 120B",
    kind: "chat",
    deployment: "pk-npu",
    // Open (2026-10-21 on `main`, 2026-08-27 on `akbl`) on instruction, with
    // the rest of the PK pool. The alias was already answering when it was
    // tagged coming soon (measured 2026-08-20).
    status: "live",
    logo: "openai",
    weightsUrl: "https://huggingface.co/openai/gpt-oss-120b",
    // Priced 2026-08-30, after shipping "Not set" since 2026-08-24. The
    // ambiguity that kept it blank is resolved: the team's rate is quoted per
    // model, not per pool, and confirmed as covering this route too. Same
    // weights, same figures as the (Global) row above, stated rather than
    // borrowed from the neighbour.
    // Ticket 064 (2026-10-26): cached input $0.10 and output $0.50, the
    // provider's list price now that cached tokens are priced on their own.
    inputPkrPerMillion: 30.08,
    cachedInputPkrPerMillion: 30.078,
    outputPkrPerMillion: 150.39,
    pedigree: GPT_OSS_PEDIGREE,
  },
  // RESTORED 2026-08-21 on request: the backend team says the route was down
  // when it was removed, and expect it back.
  //
  // **Not confirmed serving.** Re-checked 2026-08-21 and Django still answers
  // `400 unknown model alias(es)` for this id, which is a different failure
  // from a down model server: an alias the backend knows but cannot reach
  // times out or returns `proxy_error` (that is what `gpt-oss-120b(high)` and
  // `qwen3-embedding` did the same evening). "Unknown alias" means no active
  // ModelRoute carries this string at all.
  //
  // So until someone re-registers it, a caller who copies this row gets
  // `404 model not found`. Verify with the free check before assuming it is
  // fixed: `POST /api/keys` with this id in `allowed_models`. A 400 naming it
  // means it is still not registered; anything else means it is.
  {
    id: "paranine/qwen-3.8-27b",
    name: "Qwen3.8 27B",
    kind: "chat",
    // Stated by the team 2026-08-24: PK NPU pool.
    deployment: "pk-npu",
    // Open with the rest of the PK pool, on instruction (2026-10-21 here).
    //
    // CAVEAT, unchanged: this is the row measured as unregistered on
    // 2026-08-21 (Django answered `400 unknown model alias(es)`). The pool
    // being open does not by itself register an alias, so re-run the free
    // check above before treating this id as callable.
    status: "live",
    logo: "qwen",
    // Repo confirmed to exist 2026-08-20 (its model card was fetched from it).
    weightsUrl: "https://huggingface.co/Qwen/Qwen3.8-27B",
    // Repriced 2026-08-30 on instruction, down from Rs 127.92 / Rs 955.28
    // (confirmed 2026-08-20), quoted against a screenshot of the live card.
    // Independent of `status`: the rate is published whether or not the row
    // is open, and the two branches disagree about that today.
    inputPkrPerMillion: 80,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 650,
    // Source, read 2026-08-20: the Qwen3.8-27B model card on Hugging Face.
    // Unlike the other three rows this one has no separate paper, so
    // `paperUrl` is the card itself and the drawer drops the duplicate
    // "Weights on Hugging Face" link rather than printing the same URL twice.
    pedigree: {
      lab: "Qwen team, Alibaba",
      parameters: "27B, dense",
      license: "Apache 2.0",
      released: "August 2026",
      contextWindow: "262,144 tokens",
      summary:
        "The Qwen team's dense multimodal release: 27 billion parameters that read text, images and video, sized to run on a single high-memory GPU while matching models many times larger on the agentic and coding suites its card reports.",
      goodFor: [
        "Coding and software engineering, where its published scores sit close to models an order of magnitude larger",
        "Computer and browser use: the card's largest gains over its predecessor are in the desktop, browser and mobile agent suites",
        "Building on permissive terms: these are the only Apache 2.0 chat weights behind any route here",
      ],
      details: [
        { label: "Architecture", value: "Dense, 64 layers, 5,120 hidden dimensions" },
        { label: "Published modalities", value: "Text, image and video in; text out" },
        { label: "Extended context", value: "Up to 1,000,000 tokens, as published" },
      ],
      benchmarks: [
        { key: "GPQA", label: "GPQA Diamond, graduate science", score: 89.2 },
        { key: "LiveCode", label: "LiveCodeBench v6, competitive coding", score: 90.3 },
        { key: "SWE Pro", label: "SWE-bench Pro, software engineering", score: 61.7 },
        { key: "OSWorld", label: "OSWorld-Verified, computer use", score: 84.3 },
        { key: "RealWorld", label: "RealWorldQA, visual understanding", score: 85.9 },
      ],
      benchmarkNote:
        "As published on the Qwen3.8-27B model card, read 2026-08-20. Not re-measured on P/9 hardware.",
      paperUrl: "https://huggingface.co/Qwen/Qwen3.8-27B",
      paperLabel: "Qwen3.8-27B model card",
    },
  },
  {
    // ADDED 2026-10-08 from the team's handoff (`ocr-deepseek-ocr2.md`), whose
    // examples were run against the sovereign gateway on 2026-10-07 under
    // exactly this id. Document OCR over `/v1/chat/completions`: an image in,
    // Markdown out, at most 8,192 tokens a request, input and output together.
    id: "paranine/DeepSeek-OCR2",
    name: "DeepSeek-OCR 2",
    kind: "chat",
    input: "image",
    // The handoff names the sovereign gateway, not a pool; filed with the
    // other routes that gateway serves from Pakistan. Move it if the team
    // says otherwise.
    deployment: "pk-npu",
    status: "live",
    logo: "deepseek",
    // Repo confirmed to exist 2026-10-08 (200).
    weightsUrl: "https://huggingface.co/deepseek-ai/DeepSeek-OCR-2",
    lab: "DeepSeek",
    blurb:
      "Document OCR: send a scan or photo of a page and get its text back as Markdown, with headings, tables and line items kept.",
    // Quoted by the team 2026-10-07 as exactly Rs 9.0234 per million both
    // ways; it prints as Rs 9.02.
    inputPkrPerMillion: 9.0234,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: 9.0234,
    pedigree: null,
  },
  {
    // ADDED 2026-10-13 from the team's handoff (`backend-docs/decisions-laya.md`):
    // live in production since that day, answering on `POST /v1/systemone`,
    // not `/v1/chat/completions`. Text plus typed questions in (`choice`,
    // `noul`, `score`), a probability per option out, no output tokens.
    id: "paranine/laya-decision",
    name: "Laya Decision",
    kind: "decision",
    // The handoff names the sovereign gateway and no pool; filed with the
    // other routes it serves from Pakistan, like DeepSeek-OCR2. Move it if the
    // team says otherwise.
    deployment: "pk-npu",
    status: "live",
    logo: null,
    // The repository the response's own `routing.repo` names; confirmed to
    // exist 2026-10-13 (200, Apache 2.0, text classification).
    weightsUrl: "https://huggingface.co/convaiinnovations/laya",
    // The Hugging Face author of that repository.
    lab: "ConvAI Innovations",
    blurb:
      "Ask yes/no, multiple-choice and rating questions about a piece of text and get an answer with a probability for each option.",
    // Quoted 2026-10-13: US$0.0357 per million input tokens, Rs 9.94 at the
    // platform rate of 278.50. A decision produces no output tokens.
    inputPkrPerMillion: 9.94,
    cachedInputPkrPerMillion: null,
    outputPkrPerMillion: "none",
    pedigree: null,
  },
  {
    id: "paranine/qwen3-embedding",
    name: "Qwen3 Embedding 8B",
    kind: "embedding",
    // Stated by the team 2026-08-24: PK NPU pool. Corroborated by the
    // 2026-08-21 outage, which it went down in alongside gpt-oss-120b(high).
    deployment: "pk-npu",
    // Open with the rest of the PK pool, on instruction (2026-10-21 here).
    status: "live",
    logo: "qwen",
    weightsUrl: "https://huggingface.co/Qwen/Qwen3-Embedding-8B",
    inputPkrPerMillion: 2.78,
    cachedInputPkrPerMillion: null,
    // Embeddings return vectors, not tokens. A zero here would read as free
    // output rather than no output, so the column says so in words.
    outputPkrPerMillion: "none",
    // Source, read 2026-08-15: the Qwen3-Embedding-8B model card on Hugging
    // Face (which cites arxiv.org/abs/2506.05176). The MTEB rows are its
    // "mean over tasks" figures per suite. The card also documents
    // caller-selectable output dimensions down to 32; that is a property of
    // the weights, and whether the gateway forwards the `dimensions`
    // parameter is unverified, so the drawer does not claim it.
    pedigree: {
      lab: "Qwen team, Alibaba",
      parameters: "8B",
      license: "Apache 2.0",
      released: "June 2025",
      contextWindow: "32,768 tokens",
      summary:
        "The largest of the Qwen3 embedding models: a multilingual text-embedding model covering more than 100 languages, built for retrieval, clustering and semantic search.",
      goodFor: [
        "Semantic search and retrieval: the embedding half of a RAG pipeline",
        "Clustering, deduplication and classification across large text corpora",
        "Multilingual matching: queries and documents in more than 100 languages, code included, share one vector space",
      ],
      details: [
        { label: "Embedding width", value: "4,096 dimensions" },
        { label: "Languages", value: "More than 100, including code" },
      ],
      benchmarks: [
        { key: "MTEB ML", label: "MTEB multilingual, mean over tasks", score: 70.58 },
        { key: "MTEB En", label: "MTEB English v2, mean over tasks", score: 75.22 },
        { key: "C-MTEB", label: "C-MTEB Chinese, mean over tasks", score: 73.84 },
      ],
      benchmarkNote:
        "As published on the Qwen3-Embedding model card. Not re-measured on P/9 hardware.",
      paperUrl: "https://arxiv.org/abs/2506.05176",
      paperLabel: "Qwen3 Embedding technical report",
    },
  },
];

/**
 * The routes the landing page's model band shows: **every live route**, one
 * card per model, in catalogue order (2026-10-08, on instruction, when the
 * fifth opened on `main`).
 *
 * Until then the band was five hand-picked ids, partly live and partly the
 * best-known names opening next, then (from 2026-09-26) live ones swapped in
 * one by one as they opened. Deriving it from `status` ends the swapping: a
 * route shows on the home page the day its tag comes off, and a coming-soon
 * one never does, which matches a heading that promises models you can call.
 *
 * One card per model, not per route: where the same weights are served from
 * both pools (gpt-oss), the first row wins, so the band does not show the
 * same model twice. The grid is five across at `xl`, so a sixth live model
 * wraps to a second row; revisit the layout then.
 */
export const FEATURED_MODELS: CatalogModel[] = CATALOG_MODELS.filter(
  (m, i, all) =>
    m.status === "live" && all.findIndex((o) => o.status === "live" && o.name === m.name) === i,
);

/**
 * The catalogue grouped into its display sections, dropping any section that
 * has no rows. Shared by the `/models` page and anything else that has to draw
 * the whole list, so the two pools cannot end up in different orders on two
 * surfaces.
 */
export const CATALOG_SECTIONS = DEPLOYMENT_SECTIONS.map((section) => {
  const rows = CATALOG_MODELS.filter((m) => m.deployment === section.deployment);
  // Live rows first, together, then the coming-soon ones (2026-10-08, on
  // instruction); each half keeps catalogue order. A copy is reordered, never
  // `CATALOG_MODELS` itself, whose order decides the playground default.
  return {
    ...section,
    models: [
      ...rows.filter((m) => m.status === "live"),
      ...rows.filter((m) => m.status !== "live"),
    ],
  };
}).filter((section) => section.models.length > 0);

/** The routes that can answer `/v1/chat/completions`. */
export const CHAT_MODELS = CATALOG_MODELS.filter((m) => m.kind === "chat");

/**
 * What the playground offers: chat routes that are open today. A coming-soon
 * route in a picker on a surface that bills per request is an invitation to
 * a 404, so those wait here until their tag comes off. An image-input route
 * (OCR, 2026-10-08) is left out too: the playground sends text only, so it
 * would just fail. Order is inherited from `CATALOG_MODELS`, so the first live
 * chat row is the default.
 */
export const PLAYGROUND_MODELS = CHAT_MODELS.filter(
  (m) => m.status === "live" && m.input !== "image",
);

/** The routes a caller can use today, in catalogue order. */
export const LIVE_MODELS = CATALOG_MODELS.filter((m) => m.status === "live");

/**
 * How a catalogue price prints, and whether it prints recessively.
 *
 * One function because the Models page draws this table twice: stacked cards
 * below `md`, a five-column table above it. Two copies of the ternary is how
 * one layout ends up saying "Not set" while the other says "Rs 0".
 */
/**
 * The cached-input price (Ticket 064). A null is known, not missing: the
 * model has no separate cached price and its cached tokens cost the input
 * price, so it reads "Same as input" in the muted ink.
 */
export function formatCachedPrice(price: number | null): { text: string; muted: boolean } {
  if (price === null) return { text: "Same as input", muted: true };
  return { text: formatPkrPrice(price), muted: false };
}

export function formatModelPrice(price: number | "none" | null): {
  text: string;
  /** True for the two non-prices, which are set in the muted ink. */
  muted: boolean;
} {
  if (price === null) return { text: PRICE_NOT_SET, muted: true };
  if (price === "none") return { text: "No output tokens", muted: true };
  return { text: formatPkrPrice(price), muted: false };
}
